import React, { useEffect, useState, useRef } from "react";
import {
  FaPlayCircle,
  FaTrophy,
  FaFilm,
  FaBullseye,
  FaMapMarkerAlt,
  FaTv,
  FaChartBar,
  FaBullhorn,
  FaArrowRight,
  FaPlay,
  FaCheckCircle,
  FaMobileAlt
} from "react-icons/fa";
import hotstarLogo from "../assets/hotstart logo.jpg";
import hotstarVideo from "../assets/JioHotstar _ Infinite Possibilities.mp4";

/* ── Animated Counter ── */
const Counter = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const target = parseInt(end, 10);
    const dur = 1800;
    let startTs = null;
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount(Math.floor(target * ease));
      if (p < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [started, end]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export default function HotstarAds() {

  // const stats = [
  //   { end: "50",  suffix: "M+", label: "Viewers Reached",   icon: <FaTv /> },
  //   { end: "92",  suffix: "%",  label: "Ad Completion Rate", icon: <FaPlayCircle /> },
  //   { end: "100", suffix: "+",  label: "IPL Campaigns",     icon: <FaTrophy /> },
  //   { end: "10",  suffix: "x",  label: "Brand Recall",      icon: <FaBullhorn /> },
  // ];

  const services = [
    {
      icon: <FaTrophy size={28} />,
      title: "IPL Advertising",
      desc: "Reach massive, highly engaged audiences during live IPL matches, world cups, and premium tournaments.",
      gradient: "from-blue-500 to-indigo-600",
      tag: "Live Sports",
    },
    {
      icon: <FaFilm size={28} />,
      title: "OTT Video Campaigns",
      desc: "Engage viewers with high-quality, non-skippable mid-roll and pre-roll video ads on premium content.",
      gradient: "from-purple-500 to-pink-500",
      tag: "Premium Content",
    },
    {
      icon: <FaBullhorn size={28} />,
      title: "Brand Awareness",
      desc: "Build unparalleled brand recall by associating your brand with blockbuster movies and top TV shows.",
      gradient: "from-amber-400 to-orange-500",
      tag: "High Impact",
    },
    {
      icon: <FaBullseye size={28} />,
      title: "Audience Targeting",
      desc: "Target viewers precisely based on their viewing habits, demographics, language, and interests.",
      gradient: "from-emerald-400 to-teal-500",
      tag: "Precision",
    },
    {
      icon: <FaMapMarkerAlt size={28} />,
      title: "Geo Targeting",
      desc: "Run hyper-local ad campaigns reaching users in specific states, cities, or even pin codes.",
      gradient: "from-cyan-400 to-blue-500",
      tag: "Local Reach",
    },
    {
      icon: <FaTv size={28} />,
      title: "Mobile & Smart TV Ads",
      desc: "Seamlessly deliver your video ads across mobile devices, tablets, and Connected Smart TVs (CTV).",
      gradient: "from-rose-500 to-red-600",
      tag: "Omnichannel",
    },
  ];

  const included = [
    { label: "Video Ads Strategy",        sub: "Professional ad format selection (Pre-roll, Mid-roll, Masthead)." },
    { label: "Campaign Analytics",        sub: "Detailed reporting on views, completion rates, and audience engagement." },
    { label: "Creative Optimization",     sub: "Expert guidance on creating video ads that capture attention instantly." },
    { label: "Budget Management",         sub: "Maximizing your ad spend for the best possible reach during peak shows." },
    { label: "Language Targeting",        sub: "Run vernacular ads matching the user's content language preference." },
    { label: "Live Event Sponsorships",   sub: "Feature your brand during live sporting events and award shows." },
  ];

  const process = [
    { num: "01", title: "Media Planning", desc: "We map out your audience and choose the best Hotstar ad inventory." },
    { num: "02", title: "Creative Setup", desc: "We align your video assets to meet Hotstar's strict quality standards." },
    { num: "03", title: "Campaign Go-Live",desc: "Your ads start running during live matches or premium shows." },
    { num: "04", title: "Reporting",      desc: "We track views and completion rates to measure brand impact." },
  ];

  return (
    <div className="bg-[#030712] min-h-screen text-white pt-24 lg:pt-28 overflow-x-hidden selection:bg-cyan-500 selection:text-white" style={{ fontFamily: 'emoji' }}>


      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex items-center border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-[140px] animate-orb-drift" />
          <div className="absolute bottom-[-5%] left-[-5%] w-[420px] h-[420px] bg-violet-700/10 rounded-full blur-[120px] animate-orb-drift" style={{ animationDelay: "4s" }} />
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="dots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="#0EA5E9" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#dots)" />
          </svg>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#030712]/40 to-[#030712]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-16 lg:py-0">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">

            {/* Left copy */}
            <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-black tracking-[0.15em] uppercase mb-8 backdrop-blur-md animate-badge-pop">
                <FaPlayCircle className="text-sm text-[#0EA5E9]" />
                Premium OTT Advertising
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8">
                <span className="text-white">Reach Millions</span><br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#38BDF8] to-[#818CF8]">
                  with Hotstar Ads
                </span>
              </h1>

              <h3 className="text-xl sm:text-2xl font-semibold text-cyan-400 mb-4 tracking-wide">
                Engage Premium Audiences with High-Impact Video Campaigns
              </h3>

              <p className="text-gray-400 text-base md:text-lg leading-relaxed font-light mb-10 max-w-xl">
                Elevate your brand presence. Run highly targeted video campaigns during live IPL matches, premium movies, and top-rated TV shows on India's largest streaming platform.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto relative z-20">
                <a href="https://wa.me/919699938509?text=Hello%20Vishwa%20Digitech!%20I%20want%20to%20talk%20to%20an%20expert%20about%20Hotstar%20Ads." target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white rounded-2xl font-black tracking-wide hover:scale-105 transition-all duration-300 shadow-[0_0_40px_rgba(14,165,233,0.35)] w-full sm:w-auto">
                  Start Your Campaign
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                </a>
               
              </div>

              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mt-12">
                {["Live Sports", "Connected TV", "100% Brand Safe"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-500 text-xs font-semibold">
                    <FaCheckCircle className="text-cyan-500" />{t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="relative hidden lg:flex items-center justify-center animate-hero-float w-full">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0EA5E9]/20 via-blue-500/10 to-transparent rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_100px_rgba(14,165,233,0.15)] bg-[#0A0F1F] w-full">
                <img
                  src={hotstarLogo}
                  alt="Disney+ Hotstar Logo"
                  className="w-full h-[400px] lg:h-[550px] object-cover opacity-90 hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/80 via-[#030712]/20 to-transparent pointer-events-none" />
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-5 -right-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Ad Completion Rate</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">92%</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">Non-skippable impact</p>
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[#0A0F1F]/90 backdrop-blur-xl rounded-2xl border border-white/10 p-4 shadow-2xl animate-badge-pop" style={{ animationDelay: "0.2s" }}>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-1">Brand Lift</p>
                <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">High</p>
                <p className="text-[10px] text-emerald-400 font-bold mt-1">Premium association</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      

      {/* ── SERVICES ── */}
      <section className="py-24 relative z-10 border-b border-white/5 overflow-hidden">
        <div className="absolute top-20 right-0 w-[400px] h-[400px] bg-indigo-600/6 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-black tracking-widest uppercase mb-6">
              Our Expertise
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-5 leading-[1.1]">
              Hotstar Advertising<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">
                Solutions
              </span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto font-light">
              Connect with India's most affluent audiences through high-impact, targeted video advertisements on Hotstar.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-[2rem] border border-white/[0.07] hover:border-white/20 bg-[#0A0F1F]/50 backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_0_50px_rgba(79,70,229,0.1)] p-8"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${svc.gradient} opacity-0 group-hover:opacity-[0.06] transition-opacity duration-700`} />
                <div className="relative z-10">
                  <span className={`inline-block text-[10px] font-black tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-to-r ${svc.gradient} text-white mb-6 shadow-md`}>
                    {svc.tag}
                  </span>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.gradient} p-[1.5px] mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-xl`}>
                    <div className="w-full h-full bg-[#0A0F1F] rounded-2xl flex items-center justify-center text-white">
                      {svc.icon}
                    </div>
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white mb-3 tracking-tight">{svc.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base font-light">{svc.desc}</p>
                  <div className="flex items-center gap-2 mt-6 text-gray-500 group-hover:text-indigo-400 text-sm font-bold transition-colors duration-300">
                    Learn more <FaArrowRight className="group-hover:translate-x-1 transition-transform text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-24 relative z-10 border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-black tracking-widest uppercase mb-6">
            Methodology
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 tracking-tight">
            How We Execute <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Campaigns</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg font-light mb-16 max-w-xl mx-auto">
            A seamless process to get your brand streaming on millions of devices.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {process.map((p, i) => (
              <div key={i} className="relative group bg-[#0A0F1F]/50 rounded-[2rem] border border-white/[0.07] hover:border-indigo-500/30 p-8 transition-all duration-300 hover:-translate-y-1 text-left">
                <div className="absolute -inset-[1px] rounded-[2rem] bg-gradient-to-b from-indigo-500/0 via-indigo-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-[1px]" />
                <div className="relative z-10">
                  <p className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-indigo-500/40 to-blue-400/20 mb-5 leading-none">{p.num}</p>
                  <h3 className="text-lg font-black text-white mb-2">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHAT'S INCLUDED ── */}
      <section className="py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 lg:gap-20 items-center">

            <div className="relative w-full lg:w-[48%]">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#0EA5E9]/15 to-purple-500/15 rounded-[3rem] blur-2xl" />
              <div className="relative rounded-[2.5rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(14,165,233,0.1)]">
                <video
                  src={hotstarVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full object-cover h-[350px] lg:h-[450px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/70 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>

            <div className="w-full lg:w-[52%]">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-black tracking-widest uppercase mb-6">
                Campaign Deliverables
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight leading-[1.1]">
                What's Included in <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">Our Packages?</span>
              </h2>
              <p className="text-gray-400 mb-8 text-base font-light leading-relaxed">
                Everything you need to run high-performing video ads on India's top OTT platform.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {included.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-4 bg-[#0A0F1F]/60 border border-white/[0.06] rounded-2xl px-5 py-4 hover:border-indigo-500/30 hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5 shadow-lg">
                      <FaCheckCircle className="text-white text-xs" />
                    </div>
                    <div>
                      <p className="font-black text-white text-sm leading-snug">{item.label}</p>
                      <p className="text-gray-500 text-xs mt-1 leading-relaxed">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AD FORMATS ── */}
      <section className="py-24 relative z-10 border-b border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-black tracking-widest uppercase mb-6">
              Ad Placements
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-5 tracking-tight">
              High-Impact <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Ad Formats</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg font-light max-w-2xl mx-auto">
              Choose from a variety of engaging formats tailored for different campaign objectives.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Mid-Roll Video Ads", desc: "10-30s non-skippable ads played during natural breaks in shows.", icon: "⏸️" },
              { title: "Pre-Roll Ads", desc: "Short video ads played right before the user's chosen content starts.", icon: "▶️" },
              { title: "Live Sports L-Bands", desc: "L-shaped banner ads displayed seamlessly during live IPL matches.", icon: "🏏" },
              { title: "Pause Ads", desc: "Static visual ads that appear exclusively when a user hits pause.", icon: "🖼️" },
            ].map((format, i) => (
              <div key={i} className="bg-[#0A0F1F]/40 backdrop-blur-md rounded-[2rem] border border-white/[0.06] p-8 hover:border-indigo-500/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(79,70,229,0.1)]">
                <div className="text-4xl mb-6">{format.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{format.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{format.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AUDIENCE INSIGHTS ── */}
      <section className="py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center gap-16">
          <div className="w-full lg:w-1/2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 text-xs font-black tracking-widest uppercase mb-6">
              Why Hotstar?
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-tight leading-[1.1]">
              Access India's Most <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">Premium Audience</span>
            </h2>
            <p className="text-gray-400 mb-10 text-base md:text-lg font-light leading-relaxed">
              Unlike standard social media, Hotstar gives you direct access to highly engaged, affluent viewers who are actively consuming premium content.
            </p>
            <div className="space-y-6">
              {[
                { title: "High Disposable Income", desc: "Reach affluent professionals, business owners, and early adopters." },
                { title: "100% Brand Safe Environment", desc: "Your ads appear alongside verified, premium studio content." },
                { title: "Attentive Viewing", desc: "Users are highly focused on the content, ensuring your ads actually get watched." }
              ].map((insight, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center shrink-0 border border-indigo-500/30 shadow-lg mt-0.5">
                    <span className="text-indigo-400 font-bold">{i + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-lg mb-1">{insight.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{insight.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="w-full lg:w-1/2 relative">
             <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 rounded-[3rem] blur-3xl" />
             <div className="grid grid-cols-2 gap-4 relative z-10">
                <div className="bg-[#0A0F1F]/80 backdrop-blur-xl rounded-[2rem] border border-white/10 p-6 md:p-8 flex flex-col justify-center text-center h-48 md:h-56 transform lg:translate-y-8 hover:border-indigo-500/30 transition-colors">
                   <p className="text-4xl md:text-5xl font-black text-white mb-3">70%</p>
                   <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-widest font-bold">Premium Smartphone Users</p>
                </div>
                <div className="bg-gradient-to-br from-indigo-600 to-blue-600 rounded-[2rem] p-6 md:p-8 flex flex-col justify-center text-center h-48 md:h-56 shadow-2xl hover:scale-105 transition-transform">
                   <p className="text-4xl md:text-5xl font-black text-white mb-3">85%</p>
                   <p className="text-[10px] md:text-xs text-white/80 uppercase tracking-widest font-bold">Urban Audience</p>
                </div>
                <div className="bg-[#0A0F1F]/80 backdrop-blur-xl rounded-[2rem] border border-white/10 p-6 md:p-8 flex flex-col justify-center text-center h-48 md:h-56 transform lg:translate-y-8 hover:border-indigo-500/30 transition-colors">
                   <p className="text-4xl md:text-5xl font-black text-white mb-3">35+</p>
                   <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-widest font-bold">Minutes Avg. Watch Time</p>
                </div>
                <div className="bg-[#0A0F1F]/80 backdrop-blur-xl rounded-[2rem] border border-white/10 p-6 md:p-8 flex flex-col justify-center text-center h-48 md:h-56 hover:border-indigo-500/30 transition-colors">
                   <p className="text-4xl md:text-5xl font-black text-white mb-3">#1</p>
                   <p className="text-[10px] md:text-xs text-gray-400 uppercase tracking-widest font-bold">Live Sports Platform</p>
                </div>
             </div>
          </div>
        </div>
      </section>

      

    </div>
  );
}
